import React, { useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, useLocation } from 'react-router-dom';
import { useAuthStore } from './store/authStore';
import { Sparkles } from 'lucide-react';
import './i18n';

// Layouts
import { Navbar, Footer } from './layouts';

// Components
import { Redirect301 } from './components/Redirect301';

// Pages
import {
  HomePage,
  ServicesPage,
  ServiceDetailPage,
  CoursesPage,
  CourseDetailPage,
  TeachersPage,
  TeacherDetailPage,
  SchoolsPage,
  SchoolDetailPage,
  KnowledgePage,
  ArticleDetailPage,
  LoginPage,
  RegisterPage,
  ForgotPasswordPage,
  ProfilePage,
  AdminPage,
  PrivacyPolicyPage,
  LegalNoticePage,
  CookiesPolicyPage,
  TermsConditionsPage,
  ProfessionalGuidelinesPage,
  BlogPage,
  BlogArticlePage
} from './pages';

// Forms (from existing Forms.jsx)
import { BecomeTeacherForm, BecomeSchoolForm, CreateServiceForm, CreateCourseForm } from './components/Forms';
import { EditProfilePage } from './components/EditProfile';
import { EditServicePage, EditCoursePage, EditTeacherPage, EditSchoolPage } from './components/EditListings';
import PublicProfilePage from './pages/PublicProfilePage';

// Scroll to top on route change
function ScrollToTop() {
  const { pathname } = useLocation();

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [pathname]);

  return null;
}

function AppContent() {
  const { loadUser, isLoading } = useAuthStore();

  useEffect(() => {
    loadUser();
  }, [loadUser]);

  if (isLoading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-cream" style={{ backgroundColor: '#FDFBF7' }}>
        <Sparkles className="h-12 w-12 text-gold animate-pulse" style={{ color: '#D4A574' }} />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-cream" style={{ backgroundColor: '#FDFBF7' }}>
      <ScrollToTop />
      <Navbar />
      <main>
        <Routes>
          <Route path="/" element={<HomePage />} />
          
          {/* New SEO-friendly URLs */}
          <Route path="/sesiones" element={<ServicesPage />} />
          <Route path="/service/:id" element={<ServiceDetailPage />} />
          <Route path="/formacion-y-eventos" element={<CoursesPage />} />
          <Route path="/course/:id" element={<CourseDetailPage />} />
          <Route path="/profesionales" element={<TeachersPage />} />
          <Route path="/teacher/:id" element={<TeacherDetailPage />} />
          <Route path="/escuelas" element={<SchoolsPage />} />
          <Route path="/school/:id" element={<SchoolDetailPage />} />
          <Route path="/conocimiento" element={<KnowledgePage />} />
          <Route path="/article/:id" element={<ArticleDetailPage />} />
          
          {/* 301 Redirects for old URLs */}
          <Route path="/services" element={<Redirect301 to="/sesiones" />} />
          <Route path="/courses" element={<Redirect301 to="/formacion-y-eventos" />} />
          <Route path="/teachers" element={<Redirect301 to="/profesionales" />} />
          <Route path="/schools" element={<Redirect301 to="/escuelas" />} />
          <Route path="/knowledge" element={<Redirect301 to="/conocimiento" />} />
          
          {/* Auth & Profile */}
          <Route path="/login" element={<LoginPage />} />
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/forgot-password" element={<ForgotPasswordPage />} />
          <Route path="/profile" element={<ProfilePage />} />
          <Route path="/profile/edit" element={<EditProfilePage />} />
          <Route path="/admin" element={<AdminPage />} />
          
          {/* Legal pages */}
          <Route path="/privacy" element={<PrivacyPolicyPage />} />
          <Route path="/legal" element={<LegalNoticePage />} />
          <Route path="/cookies" element={<CookiesPolicyPage />} />
          <Route path="/terms" element={<TermsConditionsPage />} />
          <Route path="/professional-guidelines" element={<ProfessionalGuidelinesPage />} />
          
          {/* Forms */}
          <Route path="/become-teacher" element={<BecomeTeacherForm />} />
          <Route path="/become-school" element={<BecomeSchoolForm />} />
          <Route path="/create-service" element={<CreateServiceForm />} />
          <Route path="/create-course" element={<CreateCourseForm />} />
          <Route path="/edit-service/:id" element={<EditServicePage />} />
          <Route path="/edit-course/:id" element={<EditCoursePage />} />
          <Route path="/edit-teacher/:id" element={<EditTeacherPage />} />
          <Route path="/edit-school/:id" element={<EditSchoolPage />} />
          
          {/* Public profile & Blog */}
          <Route path="/p/:identifier" element={<PublicProfilePage />} />
          <Route path="/blog" element={<BlogPage />} />
          <Route path="/blog/:slug" element={<BlogArticlePage />} />
        </Routes>
      </main>
      <Footer />
    </div>
  );
}

function App() {
  return (
    <Router>
      <AppContent />
    </Router>
  );
}

export default App;
